import * as THREE from 'three';
import type { DeskPosition } from '@/types';

// ── Exterior Colors ──────────────────────────────────────────
export const SKY_TOP = 0x1a1a4e;
export const SKY_MID = 0x4a2d6e;
export const SKY_BOTTOM = 0xe8956a;

export const GROUND_COLOR = 0x2d5a27;

export const TREE_CANOPY_COLOR = 0x1a4a1a;
export const TREE_TRUNK_COLOR = 0x5a3a1a;

export const BUILDING_WALL_COLOR = 0xe8e0d8;
export const BUILDING_ROOF_COLOR = 0xb0a898;
export const BUILDING_DOOR_COLOR = 0x4a3a2a;
export const BUILDING_WINDOW_GLOW = 0xffcc66;

// ── Interior Colors ──────────────────────────────────────────
export const FLOOR_COLOR = 0xc49a6c;
export const WALL_COLOR = 0xe8ddd0;
export const BASEBOARD_COLOR = 0xd4c8b8;

export const DESK_COLOR = 0x3a3a3a;
export const MONITOR_FRAME_COLOR = 0x1a1a1a;
export const MONITOR_SCREEN_COLOR = 0x88ccff;
export const KEYBOARD_COLOR = 0x2a2a2a;
export const CHAIR_SEAT_COLOR = 0x3a3a3a;
export const CHAIR_BASE_COLOR = 0xaaaaaa;
export const WHITEBOARD_COLOR = 0xffffff;
export const WHITEBOARD_FRAME_COLOR = 0x888888;
export const PLANT_POT_COLOR = 0x6a4a2a;
export const PLANT_LEAF_COLOR = 0x2a6a2a;
export const WINDOW_FRAME_COLOR = 0xe0e0e0;
export const WINDOW_GLASS_COLOR = 0xaaddff;
export const BOOKSHELF_COLOR = 0x8a6a4a;
export const MUG_COLOR = 0xcc6644;
export const RUG_COLOR = 0xb8976a;

// ── Character Colors ─────────────────────────────────────────
export const SKIN_COLOR = 0xf0d0a0;
export const BODY_COLOR = 0x1a1a4a;
export const LEGS_COLOR = 0x3a3a3a;

// ── Book Colors ──────────────────────────────────────────────
export const BOOK_COLORS = [0xcc4444, 0x4488aa, 0x6a6a44, 0xaaaa66, 0x8844aa, 0xcc8844, 0x44aa66, 0xaa4466];

// ── Exterior Camera ──────────────────────────────────────────
export const EXT_CAMERA_FOV = 45;
export const EXT_CAMERA_POS = new THREE.Vector3(8, 6, 12);
export const EXT_CAMERA_LOOKAT = new THREE.Vector3(0, 1, 0);
export const EXT_CAMERA_NEAR = 0.1;
export const EXT_CAMERA_FAR = 100;

// ── Interior Camera ──────────────────────────────────────────
export const INT_CAMERA_FOV = 40;
export const INT_CAMERA_POS = new THREE.Vector3(0, 8, 16);
export const INT_CAMERA_LOOKAT = new THREE.Vector3(0, 1, 0);

// ── Room Dimensions ──────────────────────────────────────────
export const ROOM_WIDTH = 12;
export const ROOM_DEPTH = 10;
export const ROOM_HEIGHT = 3.5;

// ── Ground Dimensions ────────────────────────────────────────
export const GROUND_SIZE = 30;

// ── Building Dimensions ──────────────────────────────────────
export const BUILDING_WIDTH = 4;
export const BUILDING_HEIGHT = 3;
export const BUILDING_DEPTH = 3;

// ── Tree Positions ───────────────────────────────────────────
export const TREE_POSITIONS: [number, number, number][] = [
  [-4, 0, -2],
  [-3, 0, 3],
  [5, 0, -1],
  [6, 0, 4],
  [-5, 0, 5],
  [4, 0, -4],
  [-2, 0, -5],
  [7, 0, 1],
];

// ── Desk Layout Grid ────────────────────────────────────────
// Desks have rotation π so workers face TOWARD the camera (+Z direction)
// resolveTargetRotation() computes desk.rotation + π = 2π ≡ 0 → workers face +Z ✅
export const DESK_POSITIONS: DeskPosition[] = [
  { position: new THREE.Vector3(-2.5, 0, -2.5), rotation: Math.PI },
  { position: new THREE.Vector3(2.5, 0, -2.5), rotation: Math.PI },
  { position: new THREE.Vector3(-2.5, 0, 2.0), rotation: Math.PI },
  { position: new THREE.Vector3(2.5, 0, 2.0), rotation: Math.PI },
];

// ── Whiteboard Position ──────────────────────────────────────
// Centered on the left wall
export const WHITEBOARD_POSITION = new THREE.Vector3(-5.5, 1.8, 0);

// ── Transition Timing ────────────────────────────────────────
export const DOLLY_DURATION = 0.4;
export const FADE_IN_DURATION = 0.4;
export const FADE_OUT_DURATION = 0.5;
export const WALK_DURATION = 1.5;

// ── Animation ────────────────────────────────────────────────
export const BREATHING_SPEED = Math.PI; // ~2s full cycle
export const BREATHING_AMOUNT = 0.02;
export const TYPING_BOB_SPEED = Math.PI * 4; // ~0.5s cycle
export const TYPING_BOB_AMOUNT = 0.01;
export const WRITING_BOB_SPEED = Math.PI * 3;
export const WRITING_BOB_AMOUNT = 0.008;
export const THINKING_HEAD_SPEED = Math.PI * 0.5; // ~4s slow pondering cycle
export const THINKING_HEAD_AMOUNT = 0.08;
export const THINKING_SWAY_SPEED = Math.PI * 0.3;
export const THINKING_SWAY_AMOUNT = 0.015;

// ── Worker Names & Roles ────────────────────────────────────
export const WORKER_NAMES = ['Mike', 'Sam', 'Jordan', 'Casey'];
export const WORKER_ROLES = ['Manager', 'Designer', 'Product Manager', 'Marketing'];

// ── Onboarding Positions ────────────────────────────────────
// Manager walks to front-center of room and faces the camera
export const MGR_FRONT_CENTER = new THREE.Vector3(0.5, 0, 3.5);
export const MGR_FACE_CAMERA_ROT = Math.atan2(-0.5, 12.5); // faces camera at (0,8,16) from mgr pos (0.5,0,3.5)
// Manager walks partway toward Research desk (desk 1) for delegation
export const MGR_TOWARD_RESEARCH = new THREE.Vector3(1.5, 0, 0.5);
// Manager walks partway toward EA desk (desk 2) for phone demo
export const MGR_TOWARD_EA = new THREE.Vector3(-0.5, 0, 3.0);

// ── Ambient Behavior Destinations ────────────────────────────
export const COFFEE_MACHINE_POSITION = new THREE.Vector3(-4.5, 0, -3.5);
export const COFFEE_STAND_POSITION = new THREE.Vector3(-3.8, 0, -3.5);
export const MEETING_TABLE_POSITION = new THREE.Vector3(4.5, 0, 3.0);
export const BOOKSHELF_STAND_POSITION = new THREE.Vector3(5.0, 0, -2.0);
export const WINDOW_STAND_POSITION = new THREE.Vector3(1.5, 0, -4.0);
export const PLANT_STAND_POSITION = new THREE.Vector3(0.5, 0, -3.0);

// ── Furniture Colors (new) ──────────────────────────────────
export const COFFEE_MACHINE_COLOR = 0x2a2a2a;
export const COUNTER_COLOR = 0x5a4a3a;
export const WATER_COOLER_COLOR = 0xaaddee;
export const MEETING_TABLE_COLOR = 0x6a5a4a;
export const STOOL_COLOR = 0x4a4a4a;

// ── Pathfinding ─────────────────────────────────────────────
export const WALK_SPEED = 2.0;           // units per second
export const DESK_HALF_W = 1.1;          // Collision AABB half-width (desk 1.8/2 + 0.2 margin)
export const DESK_HALF_D = 0.65;         // Collision AABB half-depth (desk 0.7/2 + 0.3 margin)
export const CENTER_LANE_Z = -0.25;      // Center aisle between desk rows
export const LEFT_LANE_X = -(ROOM_WIDTH / 2 - 1);   // Left side aisle (= -5)
export const RIGHT_LANE_X = ROOM_WIDTH / 2 - 1;     // Right side aisle (= 5)
export const WORKER_DESK_OFFSET_Z = 1.0; // How far in front of desk workers stand (was 0.7)

// ── Ambient Engine ──────────────────────────────────────────
export const MAX_AMBIENT_WORKERS = 3;

// ── Task Execution Timing ───────────────────────────────────
export const HEARTBEAT_INTERVAL_MS = 5000;            // Server heartbeat (was 12000)
export const SIMULATED_PROGRESS_MS = 5000;            // Client simulated progress cycle (was 8000)
export const STALE_THRESHOLD_MS = 15000;              // When to start simulated progress (was 10000)
export const STUCK_WARNING_MS = 30000;                // "Taking longer than expected..."
export const CLIENT_TIMEOUT_MS = 120000;              // "Task may still be running..."

// ── Onboarding Timing ───────────────────────────────────────
export const TYPEWRITER_CHAR_DELAY = 30; // ms per character
export const BEAT_PAUSE_DURATION = 1000; // ms between beats

